/**
 * CookieManager Ultra-Optimisé pour World of Numeric - Version Performance
 * SANS LAG - CROIX VISIBLE
 */

export interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
}

export class CookieManager {
  private static preferences: CookiePreferences = {
    essential: true,
    analytics: false,
    marketing: false,
    functional: false
  };
  
  private static banner: HTMLElement | null = null;
  private static modal: HTMLElement | null = null;
  private static readonly STORAGE_KEY = 'worldofnumeric_cookie_preferences';
  private static readonly CONSENT_VERSION = 'v1.0';

  static init(): void {
    const savedPreferences = localStorage.getItem(this.STORAGE_KEY);
    if (savedPreferences) {
      try {
        const parsed = JSON.parse(savedPreferences);
        if (parsed.version === this.CONSENT_VERSION) {
          this.preferences = { ...this.preferences, ...parsed.preferences };
          this.applyPreferences();
          return;
        }
      } catch (e) {
        // En cas d'erreur, redemander le consentement
      }
    }
    this.showBanner();
  }

  private static showBanner(): void {
    if (this.banner) return;

    this.banner = document.createElement('div');
    this.banner.id = 'cookie-banner-wdn';
    this.banner.innerHTML = this.getBannerHTML();
    
    this.injectStyles();
    document.body.appendChild(this.banner);

    setTimeout(() => {
      if (this.banner) this.banner.classList.add('show');
    }, 100);

    this.attachBannerEvents();
  }

  private static getBannerHTML(): string {
    return `
      <div class="cookie-banner-content">
        <div class="cookie-banner-left">
          <div class="cookie-icon">🍪</div>
          <div class="cookie-text">
            <h3>Respect de votre vie privée</h3>
            <p>Nous utilisons des cookies pour améliorer votre expérience et analyser notre audience.</p>
          </div>
        </div>
        <div class="cookie-banner-actions">
          <button id="cookie-accept-all" class="btn-cookie btn-accept-all">
            Tout accepter
          </button>
          <button id="cookie-customize" class="btn-cookie btn-customize">
            Personnaliser
          </button>
          <button id="cookie-essential-only" class="btn-cookie btn-essential">
            Essentiel uniquement
          </button>
        </div>
      </div>
    `;
  }

  private static attachBannerEvents(): void {
    document.getElementById('cookie-accept-all')?.addEventListener('click', () => {
      this.acceptAll();
    });

    document.getElementById('cookie-essential-only')?.addEventListener('click', () => {
      this.acceptEssentialOnly();
    });

    document.getElementById('cookie-customize')?.addEventListener('click', () => {
      this.showCustomizeModal();
    });
  }

  private static showCustomizeModal(): void {
    if (this.modal) return;

    this.modal = document.createElement('div');
    this.modal.id = 'cookie-modal-wdn';
    this.modal.innerHTML = this.getModalHTML();
    // Ensure styles are present even if banner was never shown
    this.injectStyles();

    document.body.appendChild(this.modal);
    
    setTimeout(() => {
      if (this.modal) this.modal.classList.add('show');
    }, 10);

    this.attachModalEvents();
  }

  private static getModalHTML(): string {
    return `
      <div class="cookie-modal-overlay">
        <div class="cookie-modal-content">
          <div class="cookie-modal-header">
            <h2>🍪 Paramètres des cookies</h2>
            <button id="cookie-modal-close" class="cookie-modal-close">✕</button>
          </div>
          
          <div class="cookie-modal-body">
            <p class="cookie-modal-intro">
              Choisissez les types de cookies que vous souhaitez autoriser.
            </p>
            
            <div class="cookie-category essential">
              <div class="category-header">
                <h3>🔧 Cookies essentiels</h3>
                <div class="toggle-container">
                  <input type="checkbox" id="toggle-essential" checked disabled>
                  <label for="toggle-essential" class="toggle"></label>
                </div>
              </div>
              <p>Nécessaires au fonctionnement du site.</p>
            </div>

            <div class="cookie-category analytics">
              <div class="category-header">
                <h3>📊 Cookies d'analyse</h3>
                <div class="toggle-container">
                  <input type="checkbox" id="toggle-analytics" ${this.preferences.analytics ? 'checked' : ''}>
                  <label for="toggle-analytics" class="toggle"></label>
                </div>
              </div>
              <p>Nous aident à comprendre comment vous utilisez notre site.</p>
            </div>

            <div class="cookie-category marketing">
              <div class="category-header">
                <h3>🎯 Cookies marketing</h3>
                <div class="toggle-container">
                  <input type="checkbox" id="toggle-marketing" ${this.preferences.marketing ? 'checked' : ''}>
                  <label for="toggle-marketing" class="toggle"></label>
                </div>
              </div>
              <p>Permettent de vous proposer des contenus personnalisés.</p>
            </div>

            <div class="cookie-category functional">
              <div class="category-header">
                <h3>⚙️ Cookies fonctionnels</h3>
                <div class="toggle-container">
                  <input type="checkbox" id="toggle-functional" ${this.preferences.functional ? 'checked' : ''}>
                  <label for="toggle-functional" class="toggle"></label>
                </div>
              </div>
              <p>Améliorent les fonctionnalités du site.</p>
            </div>
          </div>
          
          <div class="cookie-modal-footer">
            <button id="cookie-save-preferences" class="btn-save">
              Enregistrer mes préférences
            </button>
            <button id="cookie-accept-all-modal" class="btn-accept-all">
              Tout accepter
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private static attachModalEvents(): void {
    document.getElementById('cookie-modal-close')?.addEventListener('click', () => {
      this.hideModal();
    });

    document.getElementById('cookie-save-preferences')?.addEventListener('click', () => {
      this.saveCustomPreferences();
    });

    document.getElementById('cookie-accept-all-modal')?.addEventListener('click', () => {
      this.acceptAll();
    });

    this.modal?.querySelector('.cookie-modal-overlay')?.addEventListener('click', (e) => {
      if (e.target === e.currentTarget) {
        this.hideModal();
      }
    });
  }

  private static saveCustomPreferences(): void {
    this.preferences = {
      essential: true,
      analytics: (document.getElementById('toggle-analytics') as HTMLInputElement)?.checked || false,
      marketing: (document.getElementById('toggle-marketing') as HTMLInputElement)?.checked || false,
      functional: (document.getElementById('toggle-functional') as HTMLInputElement)?.checked || false
    };

    this.savePreferences();
    this.applyPreferences();
    this.hideBanner();
    this.hideModal();
    
    this.showToast('Vos préférences ont été enregistrées');
  }

  private static acceptAll(): void {
    this.preferences = {
      essential: true,
      analytics: true,
      marketing: true,
      functional: true
    };
    
    this.savePreferences();
    this.applyPreferences();
    this.hideBanner();
    this.hideModal();
    
    this.showToast('Tous les cookies ont été acceptés');
  }

  private static acceptEssentialOnly(): void {
    this.preferences = {
      essential: true,
      analytics: false,
      marketing: false,
      functional: false
    };
    
    this.savePreferences();
    this.applyPreferences();
    this.hideBanner();
    
    this.showToast('Seuls les cookies essentiels ont été acceptés');
  }

  private static savePreferences(): void {
    const dataToSave = {
      preferences: this.preferences,
      version: this.CONSENT_VERSION,
      timestamp: new Date().toISOString()
    };
    
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(dataToSave));
  }

  private static applyPreferences(): void {
    console.log('🍪 Préférences cookies appliquées:', this.preferences);
    
    if (this.preferences.analytics) {
      console.log('📊 Analytics activé');
    }
    
    if (this.preferences.marketing) {
      console.log('🎯 Marketing cookies activés');
    }
    
    if (this.preferences.functional) {
      console.log('⚙️ Cookies fonctionnels activés');
    }
  }

  private static hideBanner(): void {
    if (this.banner) {
      this.banner.classList.remove('show');
      setTimeout(() => {
        this.banner?.remove();
        this.banner = null;
      }, 300);
    }
  }

  private static hideModal(): void {
    if (this.modal) {
      this.modal.classList.remove('show');
      setTimeout(() => {
        this.modal?.remove();
        this.modal = null;
      }, 300);
    }
  }

  private static showToast(message: string): void {
    const toast = document.createElement('div');
    toast.className = 'cookie-toast';
    toast.textContent = message;
    
    document.body.appendChild(toast);
    
    setTimeout(() => toast.classList.add('show'), 100);
    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }

  // API publique
  static getPreferences(): CookiePreferences {
    return { ...this.preferences };
  }

  static hasConsent(type: keyof CookiePreferences): boolean {
    return this.preferences[type];
  }

  static resetConsent(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    this.preferences = {
      essential: true,
      analytics: false,
      marketing: false,
      functional: false
    };
    this.showBanner();
  }

  static showPreferencesModal(): void {
    this.showCustomizeModal();
  }

  private static injectStyles(): void {
    if (document.getElementById('cookie-styles-wdn')) return;
    
    const style = document.createElement('style');
    style.id = 'cookie-styles-wdn';
    style.textContent = `
      /* BANDEAU COOKIES - ULTRA SIMPLIFIÉ */
      #cookie-banner-wdn {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        background: #2c3e50;
        color: white;
        padding: 1.5rem;
        z-index: 10000;
        transform: translateY(100%);
        transition: transform 0.3s ease;
        border-top: 3px solid #ed4c67;
      }

      #cookie-banner-wdn.show {
        transform: translateY(0);
      }

      .cookie-banner-content {
        max-width: 1200px;
        margin: 0 auto;
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 2rem;
      }

      .cookie-banner-left {
        display: flex;
        align-items: center;
        gap: 1rem;
        flex: 1;
      }

      .cookie-icon {
        font-size: 2rem;
      }

      .cookie-text h3 {
        margin: 0 0 0.5rem 0;
        color: #ff99c8;
        font-size: 1.2rem;
        font-weight: 600;
      }

      .cookie-text p {
        margin: 0;
        opacity: 0.9;
        line-height: 1.5;
      }

      .cookie-banner-actions {
        display: flex;
        gap: 0.75rem;
        flex-shrink: 0;
      }

      .btn-cookie {
        padding: 0.75rem 1.25rem;
        border: none;
        border-radius: 25px !important;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
        font-size: 0.9rem;
        min-width: 120px;
      }

      .btn-accept-all {
        background: #c2185b;
        color: #ffffff;
      }

      .btn-accept-all:hover {
        background: #a51147;
      }

      .btn-customize {
        background: #1d5f9f;
        color: #ffffff;
      }

      .btn-customize:hover {
        background: #164a7a;
      }

      .btn-essential {
        background: transparent;
        color: #bdc3c7;
        border: 1px solid #7f8c8d;
      }

      .btn-essential:hover {
        background: #7f8c8d;
        color: white;
      }

      /* MODAL COOKIES - VERSION ULTRA PERFORMANTE */
      #cookie-modal-wdn {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 20000;
        opacity: 0;
        visibility: hidden;
        transition: all 0.3s ease;
      }

      #cookie-modal-wdn.show {
        opacity: 1;
        visibility: visible;
      }

      .cookie-modal-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: rgba(9, 12, 17, 0.55);
        backdrop-filter: blur(8px) saturate(115%);
        -webkit-backdrop-filter: blur(8px) saturate(115%);
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 1rem;
      }

      .cookie-modal-content {
        background: rgba(255, 255, 255, 0.05);
        color: #ecf0f1;
        border-radius: 16px;
        max-width: 640px;
        width: 100%;
        max-height: 90vh;
        overflow-y: auto;
        border: 1px solid rgba(255, 255, 255, 0.12);
        position: relative;
        backdrop-filter: blur(18px) saturate(115%);
        -webkit-backdrop-filter: blur(18px) saturate(115%);
        box-shadow: 0 18px 40px rgba(0, 0, 0, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.08);
      }

      .cookie-modal-header {
        padding: 2rem 2rem 1rem;
        border-bottom: 1px solid rgba(255,255,255,0.1);
        position: relative;
      }

      .cookie-modal-header h2 {
        margin: 0;
        font-size: 1.5rem;
        color: #ecf0f1;
      }

      .cookie-modal-close {
        position: absolute;
        top: 1.25rem;
        right: 1.25rem;
        background: rgba(255, 255, 255, 0.1);
        border: 1px solid rgba(255, 255, 255, 0.2);
        cursor: pointer;
        color: #ecf0f1;
        width: 36px;
        height: 36px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 8px;
        font-size: 18px;
        font-weight: 500;
        z-index: 100;
        transition: all 0.2s ease;
      }

      .cookie-modal-close:hover {
        background: rgba(237, 76, 103, 0.9);
        border-color: #ed4c67;
        color: #ffffff;
        transform: scale(1.05);
      }

      .cookie-modal-body {
        padding: 0 2rem 1rem;
      }

      .cookie-modal-intro {
        margin: 0 0 1.5rem 0;
        color: #bdc3c7;
        line-height: 1.6;
      }

      .cookie-category {
        background: linear-gradient(135deg, rgba(52, 73, 94, 0.7) 0%, rgba(44, 62, 80, 0.8) 100%);
        border: 1px solid rgba(255,255,255,0.15);
        border-left: none;
        border-radius: 0 16px 16px 0;
        padding: 1.5rem;
        padding-left: 1.75rem;
        margin-bottom: 1rem;
        position: relative;
        transition: all 0.2s ease;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      }

      .cookie-category:hover {
        border-color: rgba(255,255,255,0.25);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      }

      .cookie-category::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        width: 5px;
        height: 100%;
        border-radius: 5px 0 0 5px;
        box-shadow: inset 2px 0 4px rgba(0, 0, 0, 0.2);
      }

      .cookie-category.essential::before {
        background: linear-gradient(180deg, #2ecc71 0%, #27ae60 100%);
      }

      .cookie-category.analytics::before {
        background: linear-gradient(180deg, #3498db 0%, #2980b9 100%);
      }

      .cookie-category.marketing::before {
        background: linear-gradient(180deg, #ed4c67 0%, #d63656 100%);
      }

      .cookie-category.functional::before {
        background: linear-gradient(180deg, #e67e22 0%, #d35400 100%);
      }

      .category-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 0.5rem;
      }

      .category-header h3 {
        margin: 0;
        color: #ecf0f1;
        font-size: 1.1rem;
      }

      .cookie-category p {
        margin: 0;
        color: #bdc3c7;
        font-size: 0.9rem;
        line-height: 1.5;
      }

      /* TOGGLE SWITCHES ÉLÉGANTS */
      .toggle-container {
        position: relative;
      }

      .toggle-container input[type="checkbox"] {
        opacity: 0;
        width: 0;
        height: 0;
        position: absolute;
      }

      .toggle {
        display: block;
        width: 52px;
        height: 28px;
        background: #7f8c8d;
        border-radius: 28px;
        cursor: pointer;
        transition: all 0.2s ease;
        position: relative;
        box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.1);
      }

      .toggle::after {
        content: '';
        position: absolute;
        top: 2px;
        left: 2px;
        width: 24px;
        height: 24px;
        background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
        border-radius: 50%;
        transition: all 0.2s ease;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
      }

      .toggle-container input[type="checkbox"]:checked + .toggle {
        background: linear-gradient(135deg, #ed4c67 0%, #d63656 100%);
      }

      .toggle-container input[type="checkbox"]:checked + .toggle::after {
        transform: translateX(24px);
      }

      .toggle-container input[type="checkbox"]:disabled + .toggle {
        background: linear-gradient(135deg, #2ecc71 0%, #27ae60 100%);
        cursor: not-allowed;
      }

      .toggle-container input[type="checkbox"]:disabled + .toggle::after {
        transform: translateX(24px);
      }

      .cookie-modal-footer {
        padding: 1.5rem 2rem 2rem;
        background: #2c3e50;
        border-top: 1px solid rgba(255,255,255,0.1);
        display: flex;
        gap: 1rem;
        justify-content: center;
      }

      .btn-save,
      .btn-accept-all {
        padding: 1rem 2rem;
        border: none;
        border-radius: 25px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.2s ease;
        font-size: 0.95rem;
        min-width: 160px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
      }

      .btn-save {
        background: linear-gradient(135deg, #1d5f9f 0%, #164a7a 100%);
        color: #ffffff;
      }

      .btn-save:hover {
        background: linear-gradient(135deg, #164a7a 0%, #123b63 100%);
        box-shadow: 0 4px 12px rgba(22, 74, 122, 0.35);
      }

      .btn-accept-all {
        background: linear-gradient(135deg, #c2185b 0%, #a51147 100%);
        color: #ffffff;
      }

      .btn-accept-all:hover {
        background: linear-gradient(135deg, #a51147 0%, #8c0d3a 100%);
        box-shadow: 0 4px 12px rgba(164, 17, 71, 0.35);
      }

      /* TOAST */
      .cookie-toast {
        position: fixed;
        top: 20px;
        right: 20px;
        background: #2ecc71;
        color: white;
        padding: 1rem 1.5rem;
        border-radius: 8px;
        z-index: 25000;
        transform: translateX(100%);
        transition: transform 0.3s ease;
      }

      .cookie-toast.show {
        transform: translateX(0);
      }

      /* RESPONSIVE */
      @media (max-width: 768px) {
        .cookie-banner-content {
          flex-direction: column;
          gap: 1rem;
        }

        .cookie-banner-actions {
          flex-direction: column;
          width: 100%;
        }

        .btn-cookie {
          width: 100%;
        }

        .cookie-modal-content {
          margin: 1rem;
          max-height: 95vh;
        }

        .cookie-modal-header {
          padding: 1.5rem 1.5rem 1rem;
        }

        .cookie-modal-body {
          padding: 0 1.5rem 1rem;
        }

        .cookie-modal-footer {
          padding: 1rem 1.5rem 1.5rem;
          flex-direction: column;
        }

        .btn-save,
        .btn-accept-all {
          width: 100%;
        }

        .cookie-modal-close {
          top: 1rem;
          right: 1rem;
          width: 32px;
          height: 32px;
          font-size: 16px;
        }
      }
    `;
    document.head.appendChild(style);
  }
}

export default CookieManager;
